/**
 * @file TeamTaskRow.jsx
 * @location src/components/team/TeamTaskRow.jsx
 * @role TeamTaskTable 한 행 — status·업무·담당자·우선순위·기한 셀 렌더(cols 로 선택)
 */
import { useApp } from '../../store/AppContext';
import { useMembers } from '../../store/useMembers';
import {
  ackStatusMeta, STATUS, priorityMeta,
  isOverdue, isClosed,
} from '../../data/constants';
import { Chk, DueText, StatusTag } from '../ui/SharedUI';
import { Icon } from '../ui/Icon';

/** 우선순위 태그 — 보통(기본값)은 잡음이라 표시 안 함, 긴급/낮음 같은 신호만 노출 */
const priTag = (p) => {
  if (!p || p === 'normal') return <span className="pri-none">—</span>;
  const m = priorityMeta(p);
  return <span className={`tag ${m.cls}`}>{m.label}</span>;
};

/** 표 컬럼 풀세트(전체표 기본). 드릴다운은 버킷 종류에 맞는 부분집합만 전달 */
export const DEFAULT_COLS = ['status', 'title', 'aee', 'priority', 'due'];

/**
 * @param {object} props
 * @param {import('../../data/types.js').Task} props.task
 * @param {(task: import('../../data/types.js').Task) => void} props.handleToggle  체크박스 클릭 핸들러(task 전달 — requestToggle)
 * @param {string} props.today
 * @param {string[]} [props.cols]  렌더할 컬럼 키 집합
 */
export function TeamTaskRow({ task: t, handleToggle, today, isNew, cols = DEFAULT_COLS }) {
  const { S, act } = useApp();
  const { mname } = useMembers();

  const sm = ackStatusMeta(t.status, t.acknowledgedAt);
  const od = isOverdue(t.due, t.status);
  const imminent = !od && !isClosed(t.status) && t.due === today;
  // 미래(예정) — due 가 오늘보다 뒤·미종결. 음영 처리(razor ㉑: 캘린더 아님, 표시 톤만)
  const future = !isClosed(t.status) && !!t.due && t.due > today;
  // razor: 전체 탭은 「감독자 뷰」 — 본인 담당(aee) 분만 status·완료 토글 가능.
  // 매니저라도 타인 task 의 상태 직접 변경은 담당자 핸드셰이크(읽음→완료)를 깸.
  const isOwner = t.aee === S.uid;
  // 담당자 본인의 미종결 task 만 완료 토글(Chk). 같은 표현을 체크열·제목열에서 공유.
  const ownerChk = isOwner && t.status !== STATUS.CANCELLED
    ? <Chk done={t.status === STATUS.DONE} onClick={() => handleToggle(t)} />
    : null;

  return (
    <tr className={`${od ? 'row-overdue' : imminent ? 'row-imminent' : future ? 'row-future' : ''}${isNew ? ' row-new' : ''}`}>
      {cols.includes('check') && (
        <td className="col-check">
          {ownerChk ?? <StatusTag status={t.status} cls={sm.cls} label={sm.label} />}
        </td>
      )}
      {cols.includes('status') && (
        <td>
          <StatusTag status={t.status} cls={sm.cls} label={sm.label} />
        </td>
      )}
      {cols.includes('title') && (
        <td className="col-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {!cols.includes('check') && ownerChk}
            {t.templateId && <span className="kind-badge personal" title="루틴업무"><Icon name="reset" size={10} /></span>}
            <span className={`tname clickable-title${isClosed(t.status) ? ' dn' : ''}`}
              onClick={() => act('SET_UI', { patch: { modalProg: true, progTargetId: t.id } })}
              title="클릭-상세">
              {t.title}
            </span>
          </div>
        </td>
      )}
      {cols.includes('aee') && (
        <td>
          <span className="member-chip">
            <span className="mc-name">{mname(t.aee)}</span>
          </span>
        </td>
      )}
      {cols.includes('priority') && <td style={{ whiteSpace: 'nowrap' }}>{priTag(t.priority)}</td>}
      {cols.includes('due') && <td style={{ whiteSpace: 'nowrap' }}><DueText due={t.due} status={t.status} dateKind={t.dateKind} /></td>}
    </tr>
  );
}
